import React, { useState } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import Chart from '../components/Chart';
import { getWebSocketUrl } from '../utils/websocketUrl';

// WebSocket에서 받는 Upbit 티커 데이터 구조
interface WebSocketUpbitTickerData {
  code: string;
  trade_price: number;
  trade_timestamp: string;
}

interface WebSocketUpbitMessage {
  type: string;
  data: WebSocketUpbitTickerData[];
  count: number;
  timestamp: string;
}

// Chart 컴포넌트에서 사용하는 StockData 타입
interface StockData {
  stock_code: string;
  stock_name: string;
  price: number;
  change_rate: number;
  volume: number;
  timestamp: string;
}

const RTUpbitChartBTC: React.FC = () => {
  const [data, setData] = useState<StockData[]>([]);
  const [loading, setLoading] = useState(true);

  // 코드별 최신 가격 추적 (변화율 계산용)
  const codePriceMap = React.useRef<Map<string, number>>(new Map());

  useWebSocket({
    url: getWebSocketUrl('crypto', 'rt-upbit-ticker'),
    reconnectInterval: 10000, // 10초 간격
    reconnectLimit: 3, // 최대 3번만 재연결
    onMessage: (message: WebSocketUpbitMessage) => {
      if (message.type === 'upbit_ticker' && message.data) {
        // 메시지 레벨의 timestamp를 사용
        const messageTimestamp = message.timestamp;

        // KRW-BTC만 필터링
        const btcData = message.data.filter(item => item.code === 'KRW-BTC');
        
        if (btcData.length === 0) {
          return; // KRW-BTC 데이터가 없으면 업데이트하지 않음
        }

        // 기존 데이터에 새 데이터 추가 (실시간 업데이트)
        setData(prevData => {
          // 새 데이터의 타임스탬프가 기존 데이터보다 새로운 경우에만 추가
          const latestTimestamp = prevData.length > 0 ? 
            new Date(prevData[prevData.length - 1].timestamp).getTime() : 0;

          // WebSocket에서 받은 데이터를 Chart 컴포넌트 형식으로 변환
          const convertedData = btcData.map(item => {
            // 이전 가격 가져오기 (변화율 계산용)
            const previousPrice = codePriceMap.current.get(item.code) || item.trade_price;
            const changeRate = previousPrice !== 0 
              ? ((item.trade_price - previousPrice) / previousPrice) * 100 
              : 0;

            // 최신 가격 업데이트
            codePriceMap.current.set(item.code, item.trade_price);

            const converted: StockData = {
              stock_code: item.code,
              stock_name: item.code, // 코드를 이름으로 사용 (KRW-BTC)
              price: item.trade_price,
              change_rate: changeRate,
              volume: 0, // Upbit 티커 데이터에 거래량 정보가 없음
              timestamp: messageTimestamp, // 메시지 레벨의 timestamp 사용
            };

            return converted;
          });

          const filteredNewData = convertedData.filter(item => 
            new Date(item.timestamp).getTime() > latestTimestamp
          );

          if (filteredNewData.length > 0) {
            const combinedData = [...prevData, ...filteredNewData];

            // 시간 순으로 정렬 (오름차순)
            const sortedData = combinedData.sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );

            // 중복된 timestamp 제거 (동일한 timestamp와 stock_code 조합)
            const uniqueData: StockData[] = [];
            const seenKeys = new Set<string>();

            for (const item of sortedData) {
              const key = `${item.timestamp}_${item.stock_code}`;
              if (!seenKeys.has(key)) {
                seenKeys.add(key);
                uniqueData.push(item);
              }
            }

            return uniqueData;
          }

          return prevData;
        });
      }
    },
    onOpen: () => {
      setLoading(false);
    },
    onError: (error: Event) => {
      // 에러 메시지 표시하지 않고 조용히 처리
      setLoading(false);
    },
    onClose: () => {
      // 연결 종료 메시지 표시하지 않고 조용히 처리
    }
  });

  if (loading) {
    return (
      <div className="loading-container">
        <p>WebSocket 연결 중...</p>
      </div>
    );
  }

  // 에러가 있어도 차트는 계속 표시 (현상 유지)
  return (
    <div className="realtime-upbit-chart-btc">
      <Chart
        data={data}
        loading={false}
        error={null}
        onRefetch={() => {}}
        colors={['#f59e0b']} // BTC는 주황색
        usePrice={true}
      />
    </div>
  );
};

export default RTUpbitChartBTC;

